var debounce = debounce || function(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this._scrollRafId = null;
      this._lastStickyState = null;
      this._stickyOffset = 0;
      this._boundOnScroll = null;
      this._boundAnnouncementHeightHandler = null;
      this._boundResizeOffsetHandler = null;
      this._resizeObserver = null;
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
        this._stickyOffset = this.offsetTop;
        this._boundResizeOffsetHandler = debounce(() => {
          this._stickyOffset = this.offsetTop;
          this.updateHeaderMetrics();
        }, 100);
        window.addEventListener('resize', this._boundResizeOffsetHandler, { passive: true });
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      });

      // Consolidated scroll listener with rAF coalescing
      this._boundOnScroll = () => {
        if (this._scrollRafId) return;
        this._scrollRafId = requestAnimationFrame(() => {
          this._scrollRafId = null;
          const stickyChanged = this.setStickyClass();
          if (stickyChanged) {
            this.setHeaderOffset();
          }
        });
      };
      window.addEventListener('scroll', this._boundOnScroll, { passive: true });

      this.updateHeaderMetrics();
      this._boundOnScroll();

      this._boundAnnouncementHeightHandler = debounce(() => {
        this.setAnnouncementHeight();
        this.updateHeaderMetrics();
      }, 100);
      this.setAnnouncementHeight();
      window.addEventListener('resize', this._boundAnnouncementHeightHandler, { passive: true });
      document.addEventListener('theme:announcement-height:changed', this._boundAnnouncementHeightHandler);

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => {
          this.updateHeaderMetrics();
        });
        this._resizeObserver.observe(this);
        if (this.header_section) {
          this._resizeObserver.observe(this.header_section);
        }
      }

      // Mobile Navigation transparent header support.
      setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    disconnectedCallback() {
      if (this._boundOnScroll) {
        window.removeEventListener('scroll', this._boundOnScroll);
      }
      if (this._boundAnnouncementHeightHandler) {
        window.removeEventListener('resize', this._boundAnnouncementHeightHandler);
        document.removeEventListener('theme:announcement-height:changed', this._boundAnnouncementHeightHandler);
      }
      if (this._boundResizeOffsetHandler) {
        window.removeEventListener('resize', this._boundResizeOffsetHandler);
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }
      if (this._scrollRafId) {
        cancelAnimationFrame(this._scrollRafId);
        this._scrollRafId = null;
      }
    }
    updateHeaderMetrics() {
      this.setHeaderHeight();
      this.setHeaderOffset();
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        const isSticky = window.scrollY >= this._stickyOffset && window.scrollY > 0;
        const stickyChanged = this._lastStickyState !== isSticky;
        this.classList.toggle('is-sticky', isSticky);
        this._lastStickyState = isSticky;
        return stickyChanged;
      }
      return false;
    }
    setAnnouncementHeight() {
      const primaryBar = document.querySelector('.announcement-bar-section .announcement-bar');
      const secondaryBar = document.querySelector('.fading-announcement-bar-section .fading-announcement-bar');
      const primaryHeight = primaryBar ? primaryBar.offsetHeight : 0;
      const secondaryHeight = secondaryBar ? secondaryBar.offsetHeight : 0;
      document.documentElement.style.setProperty('--primary-announcement-height', primaryHeight + 'px');
      document.documentElement.style.setProperty('--secondary-announcement-height', secondaryHeight + 'px');
      document.documentElement.style.setProperty('--announcement-height', primaryHeight + secondaryHeight + 'px');
    }
    setHeaderOffset() {
      const offset = this.classList.contains('is-sticky') ? 0 : Math.max(this.header_section.getBoundingClientRect().top, 0);
      document.documentElement.style.setProperty('--header-offset', offset + 'px');
    }
    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', this.clientHeight + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');

      // Hydrate deferred sub-menu on first open
      const tpl = detailsElement.querySelector(':scope > .mobile-menu-deferred');
      if (tpl) {
        detailsElement.appendChild(tpl.content);
        tpl.remove();
        detailsElement.querySelectorAll('summary').forEach(s => s.addEventListener('click', this.onSummaryClick.bind(this)));
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(b => b.addEventListener('click', this.onCloseButtonClick.bind(this)));
      }

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function() {
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100));
      });

    }
    resizeSubMenus() {
      let updates = [];
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;
          updates.push({ el: sub_submenu.parentElement, needsLeft: total > window.innerWidth });
        });
      });
      updates.forEach(({ el, needsLeft }) => {
        el.classList.toggle('left-submenu', needsLeft);
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
